import { createSlice, createSelector } from "@reduxjs/toolkit";
import { setAuthHeader, clearAuthHeader } from "@/Api";
import { parseJwt } from "@/auth/authUtils";

const parseToken = (token) => {
    let tokenValid = true; // needs to start as true to avoid always redirecting to logout on page load [@/auth/Auth.jsx]

    if (token && token != "null") {
        const data = parseJwt(token);

        const requiredTokenKeys = [
            "account_id",
            "person_id",
            "person_profile_id",
            "organization_id",
            "organization_public_id",
            "organizations",
            "subscription_active",
            "subscription_id",
            "is_organization_initialized",
            "first_name",
            "last_name",
            "name",
            "email",
            "profile_pic",
            "role_ids",
            "role_names",
            "permissions",
            "entitled_features",
            "subscription",
            "verified",
        ];

        if (
            data &&
            requiredTokenKeys.every((key) =>
                Object.prototype.hasOwnProperty.call(data, key),
            )
        ) {
            return {
                token,
                tokenValid: true,
                accountId: data.account_id,
                personId: data.person_id,
                personProfileId: data.person_profile_id,
                organizationId: data.organization_id,
                organizationPublicId: data.organization_public_id,
                // Sort by active org first, paused orgs last, others inbetween
                organizations: data.organizations.sort((a, b) =>
                    a.id == data.organization_id
                        ? -1
                        : b.id == data.organization_id
                          ? 1
                          : a.paused - b.paused,
                ),
                subscriptionActive: data.subscription_active,
                subscriptionId: data.subscription_id,
                isOrganizationInitialized: data.is_organization_initialized,
                firstName: data.first_name,
                lastName: data.last_name,
                name: data.name,
                email: data.email,
                profileImage: data.profile_pic,
                roleIds: data.role_ids,
                roles: data.role_names,
                permissions: data.permissions,
                entitledFeatures: data.entitled_features,
                subscription: data.subscription,
                verified: data.verified,
                isBaseOrg: data.subscription == "Template",
                isSuperAdmin: data.is_super_admin || false,
            };
        } else {
            tokenValid = false;
            if (!data) {
                return;
            }
            if (data.vendor_id != null) {
                tokenValid = true;
            }
        }
    }

    return {
        token: null,
        tokenValid: tokenValid,
        accountId: null,
        personId: null,
        personProfileId: null,
        organizationId: null,
        organizationPublicId: null,
        organizations: [],
        subscriptionActive: false,
        subscriptionId: null,
        isOrganizationInitialized: false,
        firstName: null,
        lastName: null,
        name: null,
        email: null,
        profileImage: null,
        roleIds: [],
        roles: [],
        permissions: [],
        entitledFeatures: [], // TODO: Uncomment
        subscription: null,
        verified: false,
        isBaseOrg: false,
        isSuperAdmin: false,
    };
};

if (!localStorage.getItem("deviceId")) {
    localStorage.setItem("deviceId", crypto.randomUUID());
}

const initialState = parseToken(localStorage.getItem("jwtToken"));

export const authSlice = createSlice({
    name: "auth",
    initialState: initialState,
    reducers: {
        setToken: (state, action) => {
            const tokenData = parseToken(action.payload);
            Object.keys(tokenData).forEach((key) => {
                state[key] = tokenData[key];
            });

            if (state.tokenValid) {
                setAuthHeader(state.token);
                localStorage.setItem("jwtToken", state.token);
            }
        },
        setProfileImage: (state, action) => {
            state.profileImage = action.payload;
        },
        setInvitedPerson: (state, action) => {
            state.invitedPerson = action.payload;
        },
        logout: (state) => {
            const tokenData = parseToken(null);
            Object.keys(tokenData).forEach((key) => {
                state[key] = tokenData[key];
            });

            clearAuthHeader();
            localStorage.removeItem("jwtToken");
        },
    },
});

export const { setToken, setProfileImage, logout, setInvitedPerson } =
    authSlice.actions;

export const selectIsTokenInvalid = (state) => !state.auth.tokenValid;
export const selectIsLoggedIn = (state) => state.auth.token != null;
export const selectSubscriptionActive = (state) =>
    state.auth.subscriptionActive;
export const selectSubscriptionId = (state) => state.auth.subscriptionId;
export const selectSubscription = (state) => state.auth.subscription;
export const selectIsOrganizationInitialized = (state) =>
    state.auth.isOrganizationInitialized;
export const selectIsVerified = (state) => state.auth.verified;
export const selectPermissions = (state) => state.auth.permissions;
export const selectEntitledFeatures = (state) => state.auth.entitledFeatures;
export const selectHasRole = (state, role) => state.auth.roles.includes(role);
export const selectToken = (state) => state.auth.token;
export const selectAccountId = (state) => state.auth.accountId;
export const selectEmail = (state) => state.auth.email;
export const selectPersonProfileId = (state) => state.auth.personProfileId;
export const selectPersonId = (state) => state.auth.personId;
export const selectFirstName = (state) => state.auth.firstName;
export const selectName = (state) => state.auth.name;
export const selectProfileImage = (state) => state.auth.profileImage;
export const selectIsBaseOrg = (state) => state.auth.isBaseOrg;
export const selectOrganizationId = (state) => state.auth.organizationId;
export const selectOrganizationPublicId = (state) =>
    state.auth.organizationPublicId;
export const selectActiveOrganization = (state) =>
    state.auth.organizations.find((org) => org.id == state.auth.organizationId);
export const selectOrganizations = (state) => state.auth.organizations;
export const selectInvitedPerson = (state) => state.invitedPerson;
export const selectIsSuperAdmin = (state) => state.auth.isSuperAdmin;

export const selectNavTokenContext = createSelector(
    [selectPermissions, selectIsSuperAdmin],
    (permissions, isSuperAdmin) => {
        return {
            permissions,
            isSuperAdmin,
        };
    },
);

export default authSlice.reducer;
